/**
 * The four BQL routes, one function each, every response validated at the seam.
 *
 * Tenant rides the URL path and nothing else — every request IR root is
 * `extra="forbid"` server-side, so a `tenant` key in a body is a parse error
 * rather than a silent drop. The shared `api` client threads auth, the
 * `X-AIR-Request-Tenants` header, and Sentry trace headers; these functions only
 * shape the URL, parse, and unwrap.
 *
 * Note the deliberate absence of type parameters on `api.get`/`api.post`: the
 * client casts response bodies blindly, so a type parameter would assert a shape
 * nobody checked. Left off, the body arrives as `unknown` and the compiler
 * *forces* the parse below — the guarantee is structural, not a convention.
 *
 * Per P-03 the GETs take a `signal` and the POST deliberately does not: a
 * cancelled POST resolves with no result and no error, surfacing as a UI that
 * simply never updates.
 */
import { api } from '@/lib/api'
import { API_URL } from '@/lib/constants'

import { reportDroppedRowsOnce } from '@/shared/api/utils/partial-contract'

import { parseBqlBatchResponse } from './batch-contract'
import { parseResponse } from './contract'
import { parseFieldsResponse } from './fields-contract'
import { CommonValuesResultSchema } from './generated/schema'
import { parseBqlSchema } from './schema-contract'

import type { BqlResponseSpine } from './envelope'
import type {
	BqlSchema,
	CommonValuesResult,
	FieldsResponse,
	QueryDefinition,
	Source,
} from './generated/schema'

/** Every BQL route is tenant-scoped under this prefix. */
const base = (tenant: string) => `/api/tenants/${tenant}/bql`

/**
 * Serialize logs addressing into the repeated `&source=` params discovery and
 * values expect.
 *
 * The wire form is `<definition>[:<event_type>]`. It is assembled by hand because
 * the api client's `params` option is a flat record and therefore cannot express a
 * repeated key — the exact shape both routes require.
 *
 * Exported because the query factory keys its cache on these same labels: a
 * second, parallel implementation there would let cache identity drift from the
 * request actually sent, so one source of truth is load-bearing rather than tidy.
 *
 * A second colon or an empty definition is an envelope-level 400 (the server
 * cannot key a per-source failure on a param it could not parse), so callers
 * should keep event types colon-free.
 */
export function sourceLabels(sources: readonly Source[]): string[] {
	return sources.map((source) =>
		source.event_type
			? `${source.definition}:${source.event_type}`
			: source.definition,
	)
}

/**
 * The inverse of `sourceLabels`: a compact `<definition>[:<event_type>]` label
 * back into the `Source` object the query model takes.
 *
 * Paired with its serializer on purpose — the compact form is what gets PERSISTED
 * (a widget's `querySpec.source`) and what rides the discovery query string, so
 * both directions of that one encoding belong in one place. Split on the FIRST
 * colon: a second one is a malformed label the server refuses at envelope level,
 * and treating the tail as the event type keeps that refusal legible rather than
 * silently addressing a definition nobody named.
 *
 * A label with no colon yields a bare definition — legal for `POST /bql` (it
 * addresses every stream) but not for `/bql/fields`, which requires an event type.
 */
export function parseSourceLabel(label: string): Source {
	const colon = label.indexOf(':')
	if (colon === -1) return { definition: label }

	return {
		definition: label.slice(0, colon),
		event_type: label.slice(colon + 1),
	}
}

/**
 * `GET /bql/schema` — the one descriptor every builder surface renders from.
 *
 * Always fetched live, never bundled: the contract carries no `schema_version`
 * (deliberately killed) so a runtime descriptor stays current with the backend
 * without a client release. `entity` narrows to a single descriptor; an unknown
 * name is silently omitted by the server rather than being an error, so callers
 * must not read "present in the response" as "the name I asked for was valid".
 *
 * ⚠️ "No `schema_version` makes additive drift self-healing" was the claim this
 * used to make outright — `492593ed` invalidated it. Additive drift (a new
 * `Operator`, a new `FieldType`) used to throw the WHOLE descriptor at the seam
 * this function calls into (`schema-contract.ts`'s `parseBqlSchema`), so "the
 * client just re-fetches and gets the new shape" was never true; it got the new
 * shape only after every entity, not before. What actually heals it now is that
 * seam's per-row parse, not the absence of a version — and per-row parse splits
 * additive drift FOUR ways, not two (verified against
 * `schema-contract.test.ts` and this file's `parseBqlSchema`, 2026-08-25):
 *
 *   - a new object KEY on an entity or field            → self-healing, Zod
 *     drops an unrecognised key
 *   - a new ENTITY name                                  → self-healing,
 *     `entities` is a `z.record`
 *   - a new `Operator` VALUE (a field's `operators`, or an
 *     `operator_legality` value)                         → DROPPED, not
 *     thrown — the one FIELD or `operator_legality` entry carrying it is
 *     lost, reported via `reportDroppedRowsOnce`; every sibling still renders
 *   - a new `FieldType` used as an `operator_legality` KEY → DROPPED, not
 *     thrown — same drift, a different door (`partial-contract.ts`'s
 *     `parseRecordRows` `keySchema` gate)
 *
 * Nothing on this route throws for additive drift anymore — that verification
 * also confirmed neither of the last two rows throws, contrary to its own
 * starting assumption. A DROP is still worth catching: it is silent, permanent
 * data loss (a field or an `operator_legality` entry disappears with no UI
 * disclosure — `partial-contract.ts`'s "Guardrail" comment) that today only
 * Sentry would ever surface, after the fact. `tools/check-live-descriptor.ts`
 * is what watches for this now, against deployed dev, in CI, before merge.
 */
export async function fetchBqlSchema(
	tenant: string,
	entity?: string,
	signal?: AbortSignal,
): Promise<BqlSchema> {
	const route = `${base(tenant)}/schema`
	const { data } = await api.get(route, {
		signal,
		...(entity ? { params: { entity } } : {}),
	})

	const { value, dropped } = parseBqlSchema(data, route)
	reportDroppedRowsOnce(dropped, {
		source: 'react-query',
		feature: 'bql',
		component: 'api.ts',
		action: 'fetch_bql_schema',
	})

	return value
}

/**
 * `POST /bql` — batch execution, one envelope per `query_id`.
 *
 * Returns HTTP 200 with per-tile success *or* failure; a rejected tile never
 * fails its siblings. A thrown error here therefore means the **whole request**
 * failed — door-level authn, or an envelope-structural 400 (no body, `queries`
 * not a list, a tile missing `query_id`, more than 15 tiles). Per-tile failures
 * are *not* errors and must be read off each result's `success` discriminant;
 * conflating the two is the easiest way to misread this contract.
 *
 * Only the envelope spine is validated — see `envelope.ts` for why `rows` stays
 * `unknown[]`. Parsing itself is PER TILE (`batch-contract.ts`'s
 * `parseBqlBatchResponse`): a tile carrying an unrecognised
 * `warnings[].code` or `error.code` is dropped and reported to Sentry rather
 * than throwing the whole batch — the same "a rejected tile never fails its
 * siblings" promise this docblock already states, now honoured against this
 * client's own contract mismatches too, not only against the server's.
 *

 * Two caller obligations the types cannot enforce: `query_id` must be the widget
 * id, injected around the (possibly broken) saved body so the demux key survives a
 * malformed query; and `time` must be **omitted entirely** for anchorless entities
 * (`time_anchor: null`, today `connectors`), which reject it as a `validation`
 * tile rather than ignoring it. Pre-empt that off the descriptor rather than
 * provoking the rejection.
 */
export async function runBqlBatch(
	tenant: string,
	queries: readonly QueryDefinition[],
): Promise<BqlResponseSpine> {
	const route = base(tenant)
	const { data } = await api.post(route, { queries })

	const { value, dropped } = parseBqlBatchResponse(data, route)
	reportDroppedRowsOnce(dropped, {
		source: 'react-query',
		feature: 'bql',
		component: 'api.ts',
		action: 'run_bql_batch',
	})

	return value
}

/**
 * Resolve the descriptor's `dynamic_fields_route` into a path this client can call.
 *
 * The descriptor advertises a **template** against the server's own root —
 * `/air/api/tenants/{tenant}/bql/fields` — while the api client is already based
 * at that same root. Resolving therefore means substituting the tenant and
 * dropping the prefix the client will re-add; passing the template through raw
 * would request `/air/air/...`.
 *
 * The prefix is read off `API_URL` rather than hardcoded, so a deployment served
 * from a different root (or none) stays correct without editing this file.
 *
 * Exists because the route is server-declared and must not be hardcoded: the next
 * dynamic-field entity may answer somewhere else, and a client that assumed
 * `/bql/fields` would silently query the wrong surface.
 */
export function resolveDiscoveryRoute(
	template: string,
	tenant: string,
): string {
	const path = template.replace('{tenant}', tenant)
	// Trailing slashes stripped so a root of `/air/` and `/air` behave alike.
	const basePath = new URL(API_URL).pathname.replace(/\/+$/, '')

	return basePath && path.startsWith(`${basePath}/`)
		? path.slice(basePath.length)
		: path
}

/**
 * `GET /bql/fields` — batched open-tier field discovery.
 *
 * Replaces the legacy one-request-per-`(definition, event_type)` fan-out. Every
 * requested source is echoed back as a section keyed by the exact string sent,
 * success **or** failure, inside a 200 — a silently dropped source is
 * unrepresentable in the response shape, and callers must render per-source
 * failures rather than filtering them out. `event_type` is required on every
 * source here (unlike `/bql/values`); one without it comes back as that section's
 * `validation` error.
 *
 * Unlike the batch route, entity-level gates surface as **real HTTP status**
 * (4xx), so a missing permission throws rather than arriving as a section.
 *
 * `args.route` should come from the entity descriptor's `dynamic_fields_route`
 * (via `resolveDiscoveryRoute`). It falls back to the path this route lives at
 * today only so a caller that has not yet fetched a descriptor is not blocked —
 * a descriptor-bearing caller should always pass it, because the next
 * dynamic-field entity may answer somewhere else.
 */
export async function discoverBqlFields(
	tenant: string,
	args: { entity: string; sources: readonly Source[]; route?: string },
	signal?: AbortSignal,
): Promise<FieldsResponse> {
	const route = args.route ?? `${base(tenant)}/fields`
	const search = new URLSearchParams([
		['entity', args.entity],
		...sourceLabels(args.sources).map(
			(source) => ['source', source] as [string, string],
		),
	])
	const { data } = await api.get(`${route}?${search}`, { signal })

	const { value, dropped } = parseFieldsResponse(data, route)
	reportDroppedRowsOnce(dropped, {
		source: 'react-query',
		feature: 'bql',
		component: 'api.ts',
		action: 'discover_bql_fields',
	})

	return value
}

/** A query's `time` window: absolute `{from, to}` or relative `{last}`. */
export type TimeWindow = NonNullable<QueryDefinition['time']>

/**
 * A query `time` object as the `/bql/values` `time` param: a relative window as
 * its bare `last` value (`7d`), an absolute one as JSON (URL-encoded by the
 * caller's `URLSearchParams`). The backend takes both forms.
 *
 * Exported because the query factory keys its cache on this same string, so the
 * cache identity cannot drift from the request sent.
 */
export const valuesTimeParam = (
	time: TimeWindow | undefined,
): string | undefined => {
	if (!time) return undefined
	return 'last' in time ? time.last : JSON.stringify(time)
}

/**
 * `GET /bql/values` — a field's distinct in-use values, for a value picker.
 *
 * Pure enumeration: no counts and no filter. "How do documents distribute
 * across this field's values?" is a different capability entirely — `POST /bql`
 * with `group_by` + a `count` metric. Counts on this route were proposed and
 * rejected; do not re-derive them here.
 *
 * `time` is the one piece of query context it takes: the window the values are
 * read from, so a 7-day widget offers 7-day values. Absent means the last 24h
 * server-side. Only `logs` reads it; governed entities ignore it. On `logs` the
 * values come from a per-shard SAMPLE, so their order is an estimate, not a
 * frequency ranking.
 *
 * `truncated` in the response is load-bearing: a capped list must never be
 * presented as exhaustive. `size` is clamped server-side to the entity's
 * advertised `value_discovery.max_size`.
 *
 * Two refusals a caller should tell apart from a plain failure — see
 * `valuesFailure` in `values-failure.ts`: a search that ran too long (504) and a
 * field the backend has no list of common values for (400 on `field`).
 *
 * ## Fail closed — when *not* to call this
 *
 * A rule the generated types cannot express, because absence is the signal:
 *
 * - **Entity descriptor has no `value_discovery`** ⇒ no field on that entity is
 *   value-pickable. Render a free-text input; do not probe. A future adapter
 *   without value discovery must not get a picker that always errors.
 * - **Governed tier** (`value_discovery` present, `any_field` falsy) ⇒ only fields
 *   carrying `value_source: {kind: "query"}` are pickable. A field with **no
 *   `value_source`** is free text — the two tiers answer different questions and
 *   the field list alone cannot distinguish them, which is why the descriptor
 *   advertises it.
 * - **Open tier** (`any_field: true`) ⇒ still gate on field `type`. The values
 *   path aggregates on `<field>.keyword`, so a numeric or datetime discovered
 *   field has no such subfield and returns an empty list that reads as "no values"
 *   rather than "not applicable". Offer the picker for string-typed fields only.
 * - **Either tier** ⇒ a field with `value_pickable: false` or
 *   `keyword_enabled: false` is refused. `resolveField` in
 *   `custom-dashboards/field-catalog.ts` applies all of these rules once.
 */
export async function fetchBqlValues(
	tenant: string,
	args: {
		entity: string
		field: string
		sources?: readonly Source[]
		size?: number
		time?: TimeWindow
	},
	signal?: AbortSignal,
): Promise<CommonValuesResult> {
	const route = `${base(tenant)}/values`
	const time = valuesTimeParam(args.time)
	const search = new URLSearchParams([
		['entity', args.entity],
		['field', args.field],
		...(args.size ? [['size', String(args.size)] as [string, string]] : []),
		...(time ? [['time', time] as [string, string]] : []),
		...sourceLabels(args.sources ?? []).map(
			(source) => ['source', source] as [string, string],
		),
	])
	const { data } = await api.get(`${route}?${search}`, { signal })

	return parseResponse(CommonValuesResultSchema, data, route)
}
